import React, {useCallback, useMemo, useState} from 'react'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import {Box, Grid, IconButton} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {getValidImage} from "helpers/utils";
import {saveAs} from 'file-saver';
import {downloadImageUniversal} from "helpers/cordovaDownloadImage";

export default function ImagesNew(props) {
    const {
        images,
        isScanAllowed,
        isDownloadAllowed,
        scanImage,
        isRemoveAllowed,
        removeImage,
        keepFirstImage,
        onError,
        enableImageZoom,
        /** When set, raises react-modal z-index so lightbox stacks above MUI Modal (lightbox default 1000 vs modal 1300). */
        lightboxZIndex,
        cancelEdit
    } = props

    const zoomEnabled = enableImageZoom !== false

    const imageUrls = useMemo(
        () => (images && images.length ? images.map((img) => getValidImage(img)) : []),
        [images]
    )

    const [photoIndex, setPhotoIndex] = useState(0)
    const [lightboxOpen, setLightboxOpen] = useState(false)

    const openLightbox = useCallback((index) => {
        setPhotoIndex(index)
        setLightboxOpen(true)
    }, [])

    const closeOnCancel = () => {
        cancelEdit ? cancelEdit() : setLightboxOpen(false);
    }

    if (!images || images.length === 0) {
        return <div/>
    }

    const count = images.length
    const mainSrc = imageUrls[photoIndex] || ''
    const nextSrc = count > 1 ? imageUrls[(photoIndex + 1) % count] : undefined
    const prevSrc = count > 1 ? imageUrls[(photoIndex + count - 1) % count] : undefined

    // Portrait tile (3:4); images that are wider or shorter are letterboxed via contain + max dimensions.
    const imageAreaSx = {
        width: '100%',
        aspectRatio: '3 / 4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        borderRadius: '8px',
        overflow: 'hidden',
    }

    const imgSxBase = {
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        objectPosition: 'center',
        display: 'block',
    }

    const cornerButtonSx = {
        position: 'absolute',
        zIndex: 1,
        padding: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        '&:hover': {backgroundColor: '#fff'},
    }

    return (
        <>
            {/* Match PrivateProductsMain product cells: xs={6} sm={3} (2 cols mobile, 4 cols sm+). */}
            <Grid
                container
                spacing={2}
                sx={{width: '100%', maxWidth: '100%', boxSizing: 'border-box', mb: 1.5}}
            >
                {images.map((image, i) => (
                    <Grid item xs={6} sm={3} md={3} lg={3} key={i} sx={{position: 'relative', minWidth: 0}}>
                        {isScanAllowed && scanImage && (
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => scanImage(image._id)}
                                sx={{...cornerButtonSx, top: 6, left: 6}}
                            >
                                <ManageSearchIcon fontSize="small"/>
                            </IconButton>
                        )}
                        <Box sx={imageAreaSx}>
                            <Box
                                component="img"
                                src={imageUrls[i]}
                                alt=""
                                sx={{
                                    ...imgSxBase,
                                    ...(zoomEnabled ? {cursor: 'pointer'} : {}),
                                }}
                                title={zoomEnabled ? 'Tap to view and zoom' : undefined}
                                onClick={zoomEnabled ? () => openLightbox(i) : undefined}
                                onError={() => onError(image.cdn.public_id)}
                            />
                        </Box>
                        {isRemoveAllowed && removeImage && (
                            (!keepFirstImage || i !== 0) && (
                                <IconButton
                                    size="small"
                                    color="primary"
                                    aria-label="Remove image"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeImage(image.cdn.public_id)
                                    }}
                                    sx={{...cornerButtonSx, top: 6, right: 6}}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            )
                        )}

                        {isDownloadAllowed && (
                            <IconButton
                                size="small"
                                color="primary"
                                aria-label="Download image"
                                onClick={() => {
                                    const url = getValidImage(image);
                                    const base = image?.cdn?.public_id || `photo-${i}`;
                                    downloadImageUniversal(url, base, saveAs).catch((err) => {
                                        console.error('[ImagesNew] download failed', err);
                                    });
                                }}
                                sx={{...cornerButtonSx, bottom: 6, right: 6}}
                            >
                                <DownloadIcon/>
                            </IconButton>
                        )}

                    </Grid>
                ))}
            </Grid>
            {zoomEnabled && lightboxOpen && mainSrc && (
                <Lightbox
                    mainSrc={mainSrc}
                    nextSrc={nextSrc}
                    prevSrc={prevSrc}
                    onCloseRequest={closeOnCancel}
                    onMovePrevRequest={() => setPhotoIndex((p) => (p + count - 1) % count)}
                    onMoveNextRequest={() => setPhotoIndex((p) => (p + 1) % count)}
                    enableZoom
                    {...(lightboxZIndex != null ? {
                        reactModalStyle: {
                            overlay: {zIndex: lightboxZIndex},
                            content: {zIndex: lightboxZIndex + 1},
                        },
                    } : {})}
                />
            )}
        </>
    )
}
